import React, { useMemo, useState, useCallback, useRef } from 'react';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { usePersistedDataStore } from '../../store.js';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import StableMuiDialog from '../../../common/StableMuiDialog.js';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import EvidenceComparisonToolbar from './components/EvidenceComparisonToolbar';
import {
  createEmptyRowSelection,
  createRowSelectionFromIds,
  getSelectedRowIds,
} from '../utils/gridRowSelectionModel.js';
import { withSelectAllAwareCheckbox } from '../utils/gridSelectAllCheckbox.js';
import EvidenceComparisonFooter from './components/EvidenceComparisonFooter';
import { isBARCase, isSCARCase } from '../../../utils/caseFiltering.js';
import { createEvidenceComparisonRows } from './services/rowCreationService.js';
import { isCaseSettled } from '../SummaryCaseTable/utils/helpers.js';
import axiosInstance from '../../../fetchConfig/axiosConfig.js';
import { toast } from '../../../common/toast';
import CopyableIdCell from '../../../common/CopyableIdCell.js';
import IconButton from '@mui/material/IconButton';
import DescriptionIcon from '@mui/icons-material/Description';
import Tooltip from '@mui/material/Tooltip';
import { blue } from '@mui/material/colors';
import OfferCell, { OfferEditCell } from '../SummaryCaseTable/components/GeneralizedOfferCell.js';
import { hasRoundMismatch } from '../SummaryCaseTable/config/columnDefinitions.js';
import { getMostRecentOfferRound } from '../../../utils/mostRecentOfferUtils.js';
import { getMostRecentOfferValue } from '../../../utils/mostRecentOfferUtils.js';
import { isOfferSent } from '../SummaryCaseTable/utils/helpers.js';
import { convertAVToMV, convertMVToAV, getConversionFactor } from '../../../utils/conversionUtils.js';
import { updateOffersBatch } from '../SummaryCaseTable/services/realtimeOfferService.js';
import { createOfferContext } from '../../../utils/offerContextUtils.js';
import { useUser } from '../../UserContext.js';
import WorkupViewerDialog from '../Workups/WorkupVisualization/WorkupViewerDialog.js';
import { findWorkupFile } from '../Workups/WorkupVisualization/workupFileHelpers.js';
import { handleOffersAndWorkupsEmailWorkflow, markOffersAsSent } from '../Offers/offerService.js';
import { getOfferFlowDialogFromResult } from '../Offers/offerFlowUi.js';
import { useGeneralizedEmailPreview } from '../../../hooks/useGeneralizedEmailPreview.js';
import SendSpreadsheetOffersDialog from '../Offers/SendSpreadsheetOffersDialog.js';
import CaseReviewDialogs from '../SummaryCaseTable/components/CaseReviewDialogs.js';
import { useOfferOptionsStore, workupOptionsFromDefaults } from '../Offers/offerOptionsStore.js';
import logger from '../../../utils/logger';

// Component for Download Link button with label
// Always renders, even when disabled
const DownloadLinkButton = ({ 
  label,
  file, 
  isLoading, 
  onFileView,
  allFiles,
  workupType,
  nyscefFiles = [],
  caseRow = null,
}) => {
  const hasFile = !!file;
  const hasNyscef = Array.isArray(nyscefFiles) && nyscefFiles.some((f) => f?.Url);
  const isDisabled = (!hasFile && !hasNyscef) || isLoading;
  
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'flex-end',
        width: '100%',
        height: '100%',
        visibility: 'visible !important',
      }}
    >
      <Tooltip 
        title={hasFile ? `View ${label} Workup` : hasNyscef ? 'View documents (NYSCEF)' : 'No Workup'}
        disableInteractive
        slotProps={{
          tooltip: {
            sx: {
              marginTop: '4px !important'
            }
          }
        }}
      >
        <IconButton
          size="small"
          disabled={isDisabled}
          onClick={() => {
            if (hasFile || hasNyscef) {
              onFileView(file || null, workupType, allFiles, nyscefFiles, caseRow);
            }
          }}
          sx={{
            backgroundColor: 'transparent !important',
            color: 'text.secondary',
            visibility: 'visible !important',
            display: 'flex !important',
            border: 'none !important',
            '&:hover': {
              backgroundColor: 'transparent !important',
              color: blue[700],
            },
            '&.Mui-disabled': {
              color: '#bdbdbd !important',
              visibility: 'visible !important',
              backgroundColor: 'transparent !important',
              display: 'flex !important',
              pointerEvents: 'auto',
            },
          }}
        >
          <DescriptionIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

// Component for Workup Value cell with download button
// Download button is always rendered and visible
const WorkupValueCell = ({
  value,
  impliedReduction,
  file,
  isLoading,
  onFileView,
  allFiles,
  workupType,
  nyscefFiles = [],
  caseRow = null,
}) => {
  const label = workupType === 'Unequal' ? 'Sales' : 'Assessment';
  
  if (value === null || value === undefined) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'row',
        alignItems: 'center',
        height: '100%',
        width: '100%',
        position: 'relative',
      }}>
        <span style={{ color: '#888', fontStyle: 'italic' }}>N/A</span>
        <Box sx={{
          position: 'absolute',
          right: -8,
          top: '50%',
          transform: 'translateY(-50%)',
          display: 'flex',
          alignItems: 'center',
        }}>
          <DownloadLinkButton
            label={label}
            file={file}
            isLoading={isLoading}
            onFileView={onFileView}
            allFiles={allFiles}
            workupType={workupType}
            nyscefFiles={nyscefFiles}
            caseRow={caseRow}
          />
        </Box>
      </Box>
    );
  }
  
  const formattedValue = `$${Math.round(value).toLocaleString()}`;
  
  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'row',
      alignItems: 'center',
      height: '100%',
      width: '100%',
      position: 'relative',
    }}>
      <span>
        {formattedValue}
        {impliedReduction !== null && !isNaN(impliedReduction) && (
          <span style={{ 
            color: impliedReduction >= 0 ? 'red' : 'green', 
            fontSize: '0.85em', 
            marginLeft: '4px',
            fontWeight: 'normal'
          }}>
            ({impliedReduction.toLocaleString(undefined, {
              minimumFractionDigits: 1,
              maximumFractionDigits: 1,
            })}%)
          </span>
        )}
      </span>
      <Box sx={{
        position: 'absolute',
        right: -8,
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
      }}>
        <DownloadLinkButton
          label={label}
          file={file}
          isLoading={isLoading}
          onFileView={onFileView}
          allFiles={allFiles}
          workupType={workupType}
          nyscefFiles={nyscefFiles}
          caseRow={caseRow}
        />
      </Box>
    </Box>
  );
};

// Score thresholds for workup selection logic
export const BOTH_SELECTION_THRESHOLD = 60; // If both sales and equity strength are above this, select both
export const LOW_SCORE_THRESHOLD = 40; // If scores are below this, default to sales

// Column visibility model - indices are hidden by default
const COLUMN_VISIBILITY_MODEL = {
  SalesSelectedIndexRegular: false,
  SalesSelectedIndexOptimize: false,
  AssessmentSelectedIndexRegular: false,
  AssessmentSelectedIndexOptimize: false,
};


// THE CONCEPT OF SENDING OFFERS FROM HERE HAS TO BE REDONNE.
// Not clear how / what / why sending multiples times would mean / look like.


/**
 * EvidenceComparisonTable - A table component for displaying evidence comparison data
 * Structured similarly to SummaryCaseReviewTable but for evidence comparison reports
 */
function EvidenceComparisonTable({
  negotiationCases,
}) {
  // logger.log('negotiationCases', negotiationCases);
  const apiRef = useGridApiRef();
  const getNegotiationObj = usePersistedDataStore((state) => state.negotiationObj);
  const evidenceData = usePersistedDataStore((state) => state.evidenceComparisonData || {});
  const { showDialog: showGeneralizedEmailPreview, dialogComponent: GeneralizedEmailDialog, sendEmailFromPreview } = useGeneralizedEmailPreview();
  // logger.log("evidenceData", evidenceData);
  const getIsFetching = usePersistedDataStore((state) => state.isFetching);
  const { user } = useUser();

  // Filter states
  const [activeStage, setActiveStage] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Selection model for checkbox selection
  const [selectionModel, setSelectionModel] = useState(createEmptyRowSelection);
  const selectedRowIds = useMemo(() => getSelectedRowIds(selectionModel), [selectionModel]);
  
  // Local AV toggle state (defaults to false)
  const [avToggleActive, setAvToggleActive] = useState(false);
  
  // Track workup selections per row (parcel_id -> selectedWorkupTypes array)
  const [workupSelections, setWorkupSelections] = useState({});
  
  // Loading state for Set Evidence button
  const [isSettingEvidence, setIsSettingEvidence] = useState(false);
  
  // State for Set Min Offer confirmation dialog
  const [setMinOfferDialogOpen, setSetMinOfferDialogOpen] = useState(false);
  const [offersToUpdate, setOffersToUpdate] = useState([]);
  const [isUpdatingOffers, setIsUpdatingOffers] = useState(false);
  
  // State for workup viewer dialog
  const [workupViewerOpen, setWorkupViewerOpen] = useState(false);
  const [workupViewerFiles, setWorkupViewerFiles] = useState([]);
  const [workupViewerNyscefFiles, setWorkupViewerNyscefFiles] = useState([]);
  const [workupViewerType, setWorkupViewerType] = useState('Unequal');
  const [workupViewerAddress, setWorkupViewerAddress] = useState('');
  const [workupViewerScarIndex, setWorkupViewerScarIndex] = useState('');
  const [isSendingOffersAndWorkups, setIsSendingOffersAndWorkups] = useState(false);
  const [workupOptionsDialogOpen, setWorkupOptionsDialogOpen] = useState(false);

  // Single-state offer flow dialogs (subset warning / missing offers / send-selected confirm)
  const [offerFlowDialog, setOfferFlowDialog] = useState(null);
  const offerFlowContextRef = useRef(null);
  const evidenceOfferWorkupOptionsRef = useRef(workupOptionsFromDefaults(useOfferOptionsStore.getState()));
  const offerFlowMissingOffersWithWorkupRef = useRef([]);
  const offerFlowMissingCompsCasesRef = useRef([]);
  const [updatingOffersToWorkup, setUpdatingOffersToWorkup] = useState(false);

  // Handler to update workup selection for a row
  const handleWorkupSelectionChange = useCallback((rowId, workupType, checked, currentSelection) => {
    setWorkupSelections(prev => {
      // Use the current selection from the row if available, otherwise from state
      const existingSelection = currentSelection || prev[rowId] || [];
      let newSelection;

      if (checked) {
        // Add the workup type if not already present
        newSelection = existingSelection.includes(workupType)
          ? existingSelection
          : [...existingSelection, workupType];
      } else {
        // Remove the workup type
        newSelection = existingSelection.filter(type => type !== workupType);
      }

      return {
        ...prev,
        [rowId]: newSelection,
      };
    });
  }, []);

  // Function to get display value from row's negotiations
  const getDisplayValue = useCallback((row, field) => {
    if (!row.Negotiations || !Array.isArray(row.Negotiations)) {
      return undefined;
    }

    const party = field === 'OfferValue' ? 'Aventine' : 'Town';
    return getMostRecentOfferValue(row.Negotiations, party);
  }, []);



  // Handle file view - open dialog instead of new tab
  const handleFileView = useCallback((file, workupType, allFiles, nyscefFiles = [], caseRow = null) => {
    const nyscef = Array.isArray(nyscefFiles) ? nyscefFiles : [];
    const hasNyscef = nyscef.some((f) => f?.Url);
    if ((file && file.Url) || hasNyscef) {
      setWorkupViewerFiles(Array.isArray(allFiles) ? allFiles : []);
      setWorkupViewerNyscefFiles(nyscef);
      setWorkupViewerType(workupType);
      setWorkupViewerAddress(caseRow?.Address || '');
      setWorkupViewerScarIndex(caseRow?.SCARIndexNumber || '');
      setWorkupViewerOpen(true);
    }
  }, []);

  // Calculate recommended workup selections based on business logic
    // Step 1: If only ONE of the two workups is below the IFMV, that workup should be selected
    // Step 2: If assessment case strength is >30%, and the assessment workup is < 1.06*sales workup value, then ALSO have it select the assessment workup
  const calculateRecommendedSelections = useCallback((row) => {
    const ifmv = row.IFMV;
    const salesValue = row.SalesMarketValue;
    const assessmentValue = row.AssessmentMarketValue;
    const assessmentStrength = row.AssessmentStrength;
    
    // If IFMV is not available, can't make recommendations
    if (ifmv === null || ifmv === undefined || ifmv === 0) {
      return [];
    }
    
    const recommended = [];
    
    // Base case: Select sales UNLESS sales > IFMV
    const salesBelowOrEqualIFMV = salesValue !== null && salesValue !== undefined && salesValue <= ifmv;
    if (salesBelowOrEqualIFMV) {
      recommended.push('Unequal');
    }
    
    // Step 1: If only ONE of the two workups is below the IFMV, that workup should be selected
    const assessmentBelowIFMV = assessmentValue !== null && assessmentValue !== undefined && assessmentValue < ifmv;
    
    // If sales is NOT selected (because it's > IFMV) and assessment is below IFMV, select assessment
    if (!salesBelowOrEqualIFMV && assessmentBelowIFMV) {
      recommended.push('Excessive');
    }
    
    // Step 2: If assessment case strength is >30%, and the assessment workup is < 1.06*sales workup value, then ALSO have it select the assessment workup
    if (assessmentStrength !== null && assessmentStrength !== undefined && assessmentStrength > 30) {
      if (salesValue !== null && salesValue !== undefined && 
          assessmentValue !== null && assessmentValue !== undefined) {
        const threshold = salesValue * 1.06;
        if (assessmentValue < threshold) {
          if (!recommended.includes('Excessive')) {
            recommended.push('Excessive');
          }
        }
      }
    }
    
    return recommended;
  }, []);

  // Memoize negotiation case lookup to avoid repeated array searches
  const negotiationCasesMap = useMemo(() => {
    if (!getNegotiationObj?.cases) return new Map();
    return new Map(getNegotiationObj.cases.map(c => [c.parcel_id, c]));
  }, [getNegotiationObj?.cases]);

  // Cell className function for styling selected workup cells, yellow border warnings, and offer cells
  const getCellClassName = useCallback((params) => {
    // Early return for fields that don't need styling
    const field = params.field;
    if (field !== 'OfferValue' && field !== 'SalesMarketValue' && field !== 'AssessmentMarketValue') {
      return '';
    }
    
    const selectedWorkupTypes = params.row.selectedWorkupTypes || [];
    const classes = [];
    
    // Handle OfferValue cell styling (same as SummaryCaseTable)
    if (field === 'OfferValue') {
      const row = params.row;
      const isInactive = !row.isActive;
      const notFiledAtScar = row.notFiledAtScar;

      if (row.loading || notFiledAtScar) {
        return '';
      }
    
      // Check if offer has a numeric value
      const hasOfferValue = params.value !== null && params.value !== 'None' && params.value !== '' && !isNaN(Number(params.value));

      // Use memoized map lookup instead of array.find
      const negotiationCase = negotiationCasesMap.get(row.parcel_id);
      if (negotiationCase && hasOfferValue) {
        const hasMismatch = hasRoundMismatch(negotiationCase.Negotiations);
        const aventineRound = getMostRecentOfferRound(negotiationCase.Negotiations, 'Aventine');
        const townRound = getMostRecentOfferRound(negotiationCase.Negotiations, 'Town');
        const isFromPriorRound = hasMismatch && aventineRound !== null && townRound !== null && aventineRound < townRound;
        
        if (isFromPriorRound) {
          return 'outdated-offer-cell';
        }
      }

      // If no value, red border
      if (!hasOfferValue) {
        return 'missing-offer-cell';
      }

      // Check if case is settled (inactive) - these get green background
      if (isInactive) {
        return 'settled-case-cell';
      }

      // Check finalization status using fresh data from negotiationCase
      if (negotiationCase) {
        // Check if offer is sent based on negotiations array (latest Aventine offer with LiveOffer = 1)
        const isOfferSentStatus = isOfferSent(negotiationCase.Negotiations, row.parcel_id);
        
        // If offer is sent, show finalized styling
        if (isOfferSentStatus && hasOfferValue) {
          return 'finalized-offer-cell'; // Green border, white background for sent offers
        } else if (hasOfferValue) {
          return 'pending-offer-cell'; // Yellow border for offers that have value but not sent
        }
      }

      // Fallback to pending if we can't determine status
      if (hasOfferValue) {
        return 'pending-offer-cell';
      }
      return '';
    }
    
    // Only calculate recommended selections for workup fields that need it
    const recommendedWorkupTypes = calculateRecommendedSelections(params.row);
    const isSalesSelected = selectedWorkupTypes.includes('Unequal');
    const isAssessmentSelected = selectedWorkupTypes.includes('Excessive');
    const isSalesRecommended = recommendedWorkupTypes.includes('Unequal');
    const isAssessmentRecommended = recommendedWorkupTypes.includes('Excessive');
    
    if (field === 'SalesMarketValue') {
      if (isSalesSelected) {
        classes.push('selected-sales-workup-cell');
      }
      // Yellow border on sales if sales is recommended but NOT selected
      if (!isSalesSelected && isSalesRecommended) {
        classes.push('warning-yellow-border');
      }
    }
    
    if (field === 'AssessmentMarketValue') {
      if (isAssessmentSelected) {
        classes.push('selected-assessment-workup-cell');
      }
      // Yellow border on assessment if assessment is recommended but NOT selected
      if (!isAssessmentSelected && isAssessmentRecommended) {
        classes.push('warning-yellow-border');
      }
    }
    
    return classes.join(' ');
  }, [negotiationCasesMap, calculateRecommendedSelections]);

  // Columns with separate Sales and Assessment columns
  // Market Value and Strength are grouped together for each type
  const columns = useMemo(() => [
    {
      field: 'RowIndex',
      headerName: '#',
      width: 60,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      valueFormatter: (value) => value || '',
    },
    {
      field: 'parcel_id',
      headerName: 'Parcel ID',
      width: 150,
      renderCell: (params) => (
        <CopyableIdCell 
          value={params.row.parcel_id} 
          label="Parcel ID"
        />
      ),
    },
    { field: 'Address', headerName: 'Address', width: 160 },
    {
      field: 'IFMV',
      headerName: 'IFMV',
      width: 120,
      valueFormatter: (value) => {
        if (value === null || value === undefined || value === 0) return 'N/A';
        return `$${value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
      },
    },
    // Sales Workup columns - Market Value and Strength grouped together
    {
      field: 'SalesMarketValue',
      headerName: 'Sales Workup',
      width: 160,
      renderCell: (params) => {
        const files = Array.isArray(params.row.S3Files) ? params.row.S3Files : [];
        const nyscefFiles = Array.isArray(params.row.NyscefS3Files) ? params.row.NyscefS3Files : [];
        const salesFile = findWorkupFile(files, 'Unequal');
        
        return (
          <WorkupValueCell
            value={params.row.SalesMarketValue}
            impliedReduction={params.row.SalesImpliedReduction}
            file={salesFile}
            isLoading={false}
            onFileView={handleFileView}
            allFiles={files}
            workupType="Unequal"
            nyscefFiles={nyscefFiles}
            caseRow={params.row}
          />
        );
      },
    },
    {
      field: 'SalesStrength',
      headerName: 'Sales Score',
      width: 100,
      valueFormatter: (value) => {
        if (value === null || value === undefined) return 'N/A';
        const numValue = typeof value === 'number' ? value : parseFloat(value);
        if (isNaN(numValue)) return 'N/A';
        return `${numValue.toFixed(0)}%`;
      },
    },
    {
      field: 'SalesSelectedIndexRegular',
      headerName: 'Sales Index (Regular)',
      width: 180,
      valueFormatter: (value) => {
        if (value === null || value === undefined) return 'N/A';
        if (Array.isArray(value)) {
          return value.join(', ');
        }
        return String(value);
      },
    },
    {
      field: 'SalesSelectedIndexOptimize',
      headerName: 'Sales Index (Optimize)',
      width: 190,
      valueFormatter: (value) => {
        if (value === null || value === undefined) return 'N/A';
        if (Array.isArray(value)) {
          return value.join(', ');
        }
        return String(value);
      },
    },
    // Assessment Workup columns - Market Value and Strength grouped together
    {
      field: 'AssessmentMarketValue',
      headerName: 'Assessment Workup',
      width: 160,
      renderCell: (params) => {
        const files = Array.isArray(params.row.S3Files) ? params.row.S3Files : [];
        const nyscefFiles = Array.isArray(params.row.NyscefS3Files) ? params.row.NyscefS3Files : [];
        const equityFile = findWorkupFile(files, 'Excessive');
        
        return (
          <WorkupValueCell
            value={params.row.AssessmentMarketValue}
            impliedReduction={params.row.AssessmentImpliedReduction}
            file={equityFile}
            isLoading={false}
            onFileView={handleFileView}
            allFiles={files}
            workupType="Excessive"
            nyscefFiles={nyscefFiles}
            caseRow={params.row}
          />
        );
      },
    },
    {
      field: 'AssessmentStrength',
      headerName: 'Assessment Score',
      width: 140,
      valueFormatter: (value) => {
        if (value === null || value === undefined) return 'N/A';
        const numValue = typeof value === 'number' ? value : parseFloat(value);
        if (isNaN(numValue)) return 'N/A';
        return `${numValue.toFixed(0)}%`;
      },
    },
    {
      field: 'Difference',
      headerName: 'Difference',
      type: 'number',
      width: 140,
      valueGetter: (value, row) => {
        // Return just the numeric difference for CSV export
        const salesValue = row.SalesMarketValue;
        const assessmentValue = row.AssessmentMarketValue;
        
        if (salesValue === null || salesValue === undefined || 
            assessmentValue === null || assessmentValue === undefined) {
          return null;
        }
        
        const diff = Number(assessmentValue) - Number(salesValue);
        return isNaN(diff) ? null : diff;
      },
      renderCell: (params) => {
        const salesValue = params.row.SalesMarketValue;
        const assessmentValue = params.row.AssessmentMarketValue;
        
        if (salesValue === null || salesValue === undefined || 
            assessmentValue === null || assessmentValue === undefined) {
          return <span style={{ color: '#888', fontStyle: 'italic' }}>N/A</span>;
        }
        
        const diff = assessmentValue - salesValue;
        const percentChange = salesValue !== 0 
          ? ((diff / salesValue) * 100)
          : 0;
        
        const diffColor = diff >= 0 ? 'red' : 'green';
        const formattedDiff = `$${Math.round(Math.abs(diff)).toLocaleString()}`;
        const formattedPercent = `${percentChange >= 0 ? '+' : ''}${percentChange.toFixed(1)}%`;
        
        return (
          <span>
            <span style={{ color: diffColor }}>
              {diff >= 0 ? '+' : '-'}{formattedDiff}
            </span>
            <span style={{ 
              color: diffColor, 
              fontSize: '0.85em', 
              marginLeft: '4px',
              fontWeight: 'normal'
            }}>
              ({formattedPercent})
            </span>
          </span>
        );
      },
    },
    {
      field: 'OfferValue',
      headerName: avToggleActive ? 'Aventine Offer (AV)' : 'Aventine Offer (MV)',
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      width: 140,
      editable: true,
      valueParser: (v) => (v === '' || v == null ? null : Number(v)),
      renderEditCell: (params) => {
        return (
          <OfferEditCell 
            params={params} 
            avToggleActive={avToggleActive} 
            getDisplayValue={getDisplayValue}
            onShowDialog={null}
            clearUserUpdate={null}
            setOffersChanged={null}
            party="Aventine"
          />
        );
      },
      renderCell: ({ row }) => {
        return (
          <OfferCell 
            row={row} 
            avToggleActive={avToggleActive} 
            townView={false} 
            getDisplayValue={getDisplayValue} 
            party="Aventine" 
          />
        );
      },
    },
    {
      field: 'WorkupSelection',
      headerName: 'Select Workup',
      width: 185,
      disableColumnMenu: true,
      sortable: false,
      align: 'left',
      headerAlign: 'left',
      valueGetter: (value, row) => {
        // Return the selectedWorkupTypes array for CSV export
        const selectedWorkupTypes = row.selectedWorkupTypes || [];
        return selectedWorkupTypes.length > 0 
          ? JSON.stringify(selectedWorkupTypes) 
          : '[]';
      },
      renderCell: (params) => {
        const selectedWorkupTypes = params.row.selectedWorkupTypes || [];
        const hasSales = params.row.SalesStrength !== null && params.row.SalesStrength !== undefined;
        const hasAssessment = params.row.AssessmentStrength !== null && params.row.AssessmentStrength !== undefined;
        const isSettled = params.row._isSettled;

        return (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'row', 
            gap: 1, 
            alignItems: 'center',
            height: '100%',
            width: '100%',
          }}>
            {hasSales && (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={selectedWorkupTypes.includes('Unequal')}
                    onChange={(e) => handleWorkupSelectionChange(params.row.id, 'Unequal', e.target.checked, selectedWorkupTypes)}
                    disabled={isSettled}
                    sx={{ py: 0, px: 0 }}
                  />
                }
                label="Sales"
                sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.75rem', ml: 0.5 } }}
              />
            )}
            {hasAssessment && (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={selectedWorkupTypes.includes('Excessive')}
                    onChange={(e) => handleWorkupSelectionChange(params.row.id, 'Excessive', e.target.checked, selectedWorkupTypes)}
                    disabled={isSettled}
                    sx={{ 
                      py: 0, 
                      px: 0,
                      '&.Mui-checked': {
                        color: '#9c27b0',
                      },
                    }}
                  />
                }
                label="Assessment"
                disabled={isSettled}
                sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.75rem', ml: 0.5 } }}
              />
            )}
            {!hasSales && !hasAssessment && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                N/A
              </Typography>
            )}
          </Box>
        );
      },
    },

    {
      field: 'Status',
      headerName: 'Status',
      width: 80,
      renderCell: (params) => {
        const isSettled = params.row._isSettled;
        return (
          <Box
            sx={{
              color: isSettled ? '#1b5e20' : '#e65100',
              fontWeight: 'bold',
              fontSize: '0.75rem',
            }}
          >
            {isSettled ? 'Settled' : 'Open'}
          </Box>
        );
      },
    },

    {
      field: 'AssessmentSelectedIndexRegular',
      headerName: 'Assessment Index (Regular)',
      width: 200,
      valueFormatter: (value) => {
        if (value === null || value === undefined) return 'N/A';
        if (Array.isArray(value)) {
          return value.join(', ');
        }
        return String(value);
      },
    },
    {
      field: 'AssessmentSelectedIndexOptimize',
      headerName: 'Assessment Index (Optimize)',
      width: 210,
      valueFormatter: (value) => {
        if (value === null || value === undefined) return 'N/A';
        if (Array.isArray(value)) {
          return value.join(', ');
        }
        return String(value);
      },
    },
  ], [handleWorkupSelectionChange, handleFileView, avToggleActive]);

  // Apply filtering logic to cases
  const filteredCases = useMemo(() => {
    if (!negotiationCases || !Array.isArray(negotiationCases)) return [];
    
    return negotiationCases.filter((caseItem) => {
      // Apply stage filter
      if (activeStage && activeStage !== 'all') {
        let shouldInclude = false;
        if (activeStage === 'BAR' && isBARCase(caseItem)) {
          shouldInclude = true;
        } else if (activeStage === 'SCAR' && isSCARCase(caseItem)) {
          shouldInclude = true;
        }
        if (!shouldInclude) {
          return false;
        }
      }

      // Apply status filter
      if (statusFilter && statusFilter !== 'all') {
        const caseSettled = isCaseSettled(caseItem);
        const caseStatus = caseSettled ? 'Settled' : 'Unsettled';
        if (statusFilter !== caseStatus) {
          return false;
        }
      }

      return true;
    });
  }, [negotiationCases, activeStage, statusFilter]);

  // Fetch S3 files for all parcels once when filtered cases are available
  // This runs outside of render cycle

  // Create rows from filtered cases, combining Sales and Assessment evidence data
  const rows = useMemo(() => {
    const baseRows = createEvidenceComparisonRows(filteredCases, evidenceData);
    
    // Merge with persisted selections, using default if no selection exists
    return baseRows.map(row => ({
      ...row,
      selectedWorkupTypes: workupSelections[row.id] ?? row.selectedWorkupTypes ?? [],
    }));
  }, [filteredCases, evidenceData, workupSelections]);

  // Check if all selected rows with sales data have sales selected (excluding settled cases)
  const areAllSalesSelected = useMemo(() => {
    const rowsWithSales = rows.filter(row => 
      selectedRowIds.includes(row.id) && // Only selected rows
      !row._isSettled && 
      row.SalesStrength !== null && row.SalesStrength !== undefined
    );
    if (rowsWithSales.length === 0) return false;
    
    return rowsWithSales.every(row => {
      const selectedWorkupTypes = row.selectedWorkupTypes || [];
      return selectedWorkupTypes.includes('Unequal');
    });
  }, [rows, selectionModel]);

  // Check if all selected rows with assessment data have assessment selected (excluding settled cases)
  const areAllAssessmentsSelected = useMemo(() => {
    const rowsWithAssessment = rows.filter(row => 
      selectedRowIds.includes(row.id) && // Only selected rows
      !row._isSettled && 
      row.AssessmentStrength !== null && row.AssessmentStrength !== undefined
    );
    if (rowsWithAssessment.length === 0) return false;
    
    return rowsWithAssessment.every(row => {
      const selectedWorkupTypes = row.selectedWorkupTypes || [];
      return selectedWorkupTypes.includes('Excessive');
    });
  }, [rows, selectionModel]);

  // Handle Select/Deselect All Sales
  const handleSelectAllSales = useCallback(() => {
    if (selectedRowIds.length === 0) {
      toast.error('Please select at least one row', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }
    
    setWorkupSelections(prev => {
      const newSelections = { ...prev };
      const shouldSelect = !areAllSalesSelected;
      
      rows
        .filter(row => selectedRowIds.includes(row.id)) // Only selected rows
        .forEach(row => {
        // Skip settled cases
        if (row._isSettled) return;
        
        // Only affect rows that have sales data available
        const hasSales = row.SalesStrength !== null && row.SalesStrength !== undefined;
        if (hasSales) {
          const currentSelection = newSelections[row.id] || row.selectedWorkupTypes || [];
          if (shouldSelect) {
            // Select: add if not already present
            if (!currentSelection.includes('Unequal')) {
              newSelections[row.id] = [...currentSelection, 'Unequal'];
            }
          } else {
            newSelections[row.id] = currentSelection.filter(type => type !== 'Unequal');
          }
        }
        });
      return newSelections;
    });
  }, [rows, areAllSalesSelected, selectionModel]);

  // Handle Select/Deselect All Assessments
  const handleSelectAllAssessments = useCallback(() => {
    if (selectedRowIds.length === 0) {
      toast.error('Please select at least one row', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }
    
    setWorkupSelections(prev => {
      const newSelections = { ...prev };
      const shouldSelect = !areAllAssessmentsSelected;
      
      rows
        .filter(row => selectedRowIds.includes(row.id)) // Only selected rows
        .forEach(row => {
        // Skip settled cases
        if (row._isSettled) return;
        
        // Only affect rows that have assessment data available
        const hasAssessment = row.AssessmentStrength !== null && row.AssessmentStrength !== undefined;
        if (hasAssessment) {
          const currentSelection = newSelections[row.id] || row.selectedWorkupTypes || [];
          if (shouldSelect) {
            // Select: add if not already present
            if (!currentSelection.includes('Excessive')) {
              newSelections[row.id] = [...currentSelection, 'Excessive'];
            }
          } else {
            newSelections[row.id] = currentSelection.filter(type => type !== 'Excessive');
          }
        }
        });
      return newSelections;
    });
  }, [rows, areAllAssessmentsSelected, selectionModel]);

  // Handle Set Evidence button click
  const handleSetEvidence = useCallback(async () => {
    const taxYear = getNegotiationObj?.TaxYear;
    
    if (!taxYear) {
      toast.error('Tax Year is required to update evidence submission', {
        position: 'top-right',
        autoClose: 5000,
      });
      return;
    }
    
    if (selectedRowIds.length === 0) {
      toast.error('Please select at least one row', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }
    
    const evidenceSelectionData = rows
      .filter(row => selectedRowIds.includes(row.id)) // Only selected rows
      .filter(row => !row._isSettled) // Filter out settled cases
      .map(row => ({
        ParcelID: row.parcel_id,
        Year: taxYear,
        Evidence: [...(row.selectedWorkupTypes || [])],
        SCARIndexNumber: row.SCARIndexNumber,
      }))
      .filter(entry => entry.ParcelID && entry.Evidence.length > 0); // Filter out entries without parcel_id or evidence
    
    if (evidenceSelectionData.length === 0) {
      toast.info('No evidence selections to update', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }
    
    setIsSettingEvidence(true);
    
    try {
      logger.log('data coming in to update evidence submission', evidenceSelectionData);
      logger.log('user', user);
      const response = await axiosInstance.post('/update_evidence_submission', {
        evidenceSelections: evidenceSelectionData,
        userId: user?.userId,
      });
      
      if (response.data.success) {
        const { updated_count } = response.data;
        
        const message = `Successfully updated ${updated_count} record(s) in Court.Evidence.`;
        
        toast.success(message, {
          position: 'top-right',
          autoClose: 5000,
          closeOnClick: true,
          draggable: true,
        });
      } else {
        toast.error(response.data.error || 'Failed to update evidence submission', {
          position: 'top-right',
          autoClose: 5000,
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update evidence submission';
      logger.error('Error updating evidence submission:', errorMessage, error);
    } finally {
      setIsSettingEvidence(false);
    }
  }, [rows, getNegotiationObj, selectionModel, user]);

  const getProprietyWorkupForParcel = useCallback((parcelId) => {
    if (!parcelId || !rows || !rows.length) return null;
    const row = rows.find(r => (r.parcel_id || r.id) === parcelId);
    if (!row) return null;
    const salesValue = row.SalesMarketValue;
    const assessmentValue = row.AssessmentMarketValue;
    const values = [salesValue, assessmentValue].filter(v => v !== null && v !== undefined && v !== 0);
    if (!values.length) return null;
    return Math.min(...values);
  }, [rows]);

  // Shared helper to construct rows for offer/workup workflows
  const buildSelectedRowsForOffers = useCallback((selectedRowsFromTable, fullCases) => {
    return selectedRowsFromTable.map(r => {
      const baseCase = fullCases.find(c => (c.parcel_id || c.id) === (r.parcel_id || r.id)) ?? r;
      const parcelId = r.parcel_id || r.id;
      const proprietyWorkup = getProprietyWorkupForParcel(parcelId);
      const offerValueAV = getDisplayValue(r, 'OfferValue'); // Always use AV (native unit)

      return {
        ...baseCase,
        OfferValue: offerValueAV,
        ProprietyWorkup: proprietyWorkup,
        Comps: r.Comps ?? baseCase.Comps,
        SelectedEvidenceTypes: r.selectedWorkupTypes ?? baseCase.SelectedEvidenceTypes,
        S3Files: r.S3Files ?? baseCase.S3Files,
        NyscefS3Files: r.NyscefS3Files ?? baseCase.NyscefS3Files,
      };
    });
  }, [getProprietyWorkupForParcel, getDisplayValue]);

  const handleConfirmSendSelected = useCallback(async () => {
    const ctx = offerFlowContextRef.current;
    if (!ctx?.rerun) return setOfferFlowDialog(null);

    setIsSendingOffersAndWorkups(true);

    try {
      const { nextResult: result, nextSelectedRows } = await ctx.rerun({
        skipSubsetCheck: true,
        skipWorkupsAboveIfmvCheck: !!ctx.skipWorkupsAboveIfmv,
      });

      if (!result?.success) {
        const ui = getOfferFlowDialogFromResult({
          result,
          selectedRows: nextSelectedRows || ctx.selectedRows,
        });

        if (ui) {
          offerFlowContextRef.current.openCasesNotSelected = ui.context.openCases || [];
          if (ui.context.missingOffers) offerFlowMissingOffersWithWorkupRef.current = ui.context.missingOffers;
          if (ui.context.missingCompsCases) offerFlowMissingCompsCasesRef.current = ui.context.missingCompsCases;
          setOfferFlowDialog(ui.dialog);
          setIsSendingOffersAndWorkups(false);
          return;
        }

        toast.error(result.error || 'Validation failed for offers', { autoClose: false });
        setIsSendingOffersAndWorkups(false);
        return;
      }

      // Success: close the subset warning and continue to email preview/send.
      setOfferFlowDialog(null);

      if (result?.emailOptions) {
        const wo = ctx.workupOptions || { includeWorkups: false };
        const batchAttach = !!wo.includeWorkups && wo.type === 'batch';
        await showGeneralizedEmailPreview(result.emailOptions, async (confirmed, emailPreviewData) => {
          if (!confirmed || !emailPreviewData) {
            setIsSendingOffersAndWorkups(false);
            return;
          }

          try {
            const sendResult = await sendEmailFromPreview(emailPreviewData);
            if (sendResult.success) {
              try {
                const emailData = emailPreviewData.emailData;
                if (emailData?.data && Array.isArray(emailData.data)) {
                  const parcelIds = emailData.data
                    .filter(item => item.parcel_id)
                    .map(item => item.parcel_id);
                  if (parcelIds.length > 0) {
                    await markOffersAsSent(parcelIds, ctx.negotiationObj, user);
                  }
                }
              } catch (error) {
                logger.error('❌ [EVIDENCE TABLE] Error marking offers as sent:', error);
              }

              toast.success(
                batchAttach
                  ? 'Offers and workups email sent successfully'
                  : wo.type === 'individual'
                    ? 'Offer email sent successfully (workup links in spreadsheet)'
                    : 'Offer email sent successfully'
              );
            } else {
              toast.error(`Failed to send email: ${sendResult.error}`, { autoClose: false });
            }
          } catch (err) {
            logger.error('❌ [EVIDENCE TABLE] Error sending offer email:', err);
            toast.error(err?.message || 'Error sending email', { autoClose: false });
          } finally {
            setIsSendingOffersAndWorkups(false);
          }
        });
      } else {
        setIsSendingOffersAndWorkups(false);
      }
    } catch (error) {
      logger.error('❌ [EVIDENCE TABLE] Error in confirm send selected:', error);
      toast.error(error?.message || 'Error sending offers', { autoClose: false });
      setIsSendingOffersAndWorkups(false);
    }
  }, [
    getOfferFlowDialogFromResult,
    showGeneralizedEmailPreview,
    sendEmailFromPreview,
    user,
    setIsSendingOffersAndWorkups,
    markOffersAsSent,
    toast,
  ]);

  const handleContinueWorkupsAboveIfmv = useCallback(() => {
    if (offerFlowContextRef.current) offerFlowContextRef.current.skipWorkupsAboveIfmv = true;
    return handleConfirmSendSelected();
  }, [handleConfirmSendSelected]);

  // dumb duplicated code that exists in the customtoolbar of summarycasereviewtablerebuilt as well. Should be abstracted in the future.
  const handleSendAllFinalized = useCallback(async () => {
    const ctx = offerFlowContextRef.current;
    if (!ctx?.rerun) return setOfferFlowDialog(null);

    const openParcelIds = (ctx.openCasesNotSelected || []).map((c) => c.parcel_id || c.id).filter(Boolean);
    const additionalRowsFromTable = rows.filter((r) => openParcelIds.includes(r.parcel_id || r.id));
    const combinedFromTable = [...(ctx.selectedRowsFromTable || []), ...additionalRowsFromTable];
    const updatedSelectedRows = buildSelectedRowsForOffers(combinedFromTable, ctx.fullCases || []);

    setIsSendingOffersAndWorkups(true);

    try {
      const { nextResult: result, nextSelectedRows } = await ctx.rerun({
        selectedRows: updatedSelectedRows,
        skipSubsetCheck: true,
      });

      if (!result?.success) {
        const ui = getOfferFlowDialogFromResult({
          result,
          selectedRows: nextSelectedRows || updatedSelectedRows,
        });

        if (ui) {
          offerFlowContextRef.current.selectedRows = nextSelectedRows || updatedSelectedRows;
          offerFlowContextRef.current.openCasesNotSelected = ui.context.openCases || [];
          if (ui.context.missingOffers) offerFlowMissingOffersWithWorkupRef.current = ui.context.missingOffers;
          if (ui.context.missingCompsCases) offerFlowMissingCompsCasesRef.current = ui.context.missingCompsCases;
          setOfferFlowDialog(ui.dialog);
          setIsSendingOffersAndWorkups(false);
          return;
        }

        toast.error(result.error || 'Validation failed for offers', { autoClose: false });
        setIsSendingOffersAndWorkups(false);
        return;
      }

      setOfferFlowDialog(null);

      if (result?.emailOptions) {
        const wo = ctx.workupOptions || { includeWorkups: false };
        const batchAttach = !!wo.includeWorkups && wo.type === 'batch';
        await showGeneralizedEmailPreview(result.emailOptions, async (confirmed, emailPreviewData) => {
          if (!confirmed || !emailPreviewData) {
            setIsSendingOffersAndWorkups(false);
            return;
          }

          try {
            const sendResult = await sendEmailFromPreview(emailPreviewData);
            if (sendResult.success) {
              try {
                const emailData = emailPreviewData.emailData;
                if (emailData?.data && Array.isArray(emailData.data)) {
                  const parcelIds = emailData.data
                    .filter(item => item.parcel_id)
                    .map(item => item.parcel_id);
                  if (parcelIds.length > 0) {
                    await markOffersAsSent(parcelIds, ctx.negotiationObj, user);
                  }
                }
              } catch (error) {
                logger.error('❌ [EVIDENCE TABLE] Error marking offers as sent:', error);
              }

              toast.success(
                batchAttach
                  ? 'Offers and workups email sent successfully'
                  : wo.type === 'individual'
                    ? 'Offer email sent successfully (workup links in spreadsheet)'
                    : 'Offer email sent successfully'
              );
            } else {
              toast.error(`Failed to send email: ${sendResult.error}`, { autoClose: false });
            }
          } catch (err) {
            logger.error('❌ [EVIDENCE TABLE] Error sending offer email:', err);
            toast.error(err?.message || 'Error sending email', { autoClose: false });
          } finally {
            setIsSendingOffersAndWorkups(false);
          }
        });
      } else {
        setIsSendingOffersAndWorkups(false);
      }
    } catch (error) {
      logger.error('❌ [EVIDENCE TABLE] Error in send all finalized:', error);
      toast.error(error?.message || 'Error sending offers', { autoClose: false });
      setIsSendingOffersAndWorkups(false);
    }
  }, [
    rows,
    buildSelectedRowsForOffers,
    getOfferFlowDialogFromResult,
    showGeneralizedEmailPreview,
    sendEmailFromPreview,
    user,
    setIsSendingOffersAndWorkups,
    markOffersAsSent,
    toast,
  ]);

  const handleSetOffersToWorkup = useCallback(async () => {
    const ctx = offerFlowContextRef.current;
    if (!ctx?.rerun) return setOfferFlowDialog(null);

    setUpdatingOffersToWorkup(true);
    try {
      const missingOffers = offerFlowMissingOffersWithWorkupRef.current || [];

      const updatedSelectedRows = (ctx.selectedRows || []).map((row) => {
        const missingOffer = missingOffers.find((o) => o.parcel_id === row.parcel_id);
        if (!missingOffer) return row;
        const mv = missingOffer.ProprietyWorkup;
        if (mv === null || mv === undefined || mv === '' || Number.isNaN(Number(mv)) || Number(mv) <= 0) return row;
        const conversionFactor = getConversionFactor(row.EQ, row.RAR);
        const offerValueAV = conversionFactor > 0 ? Math.round(Number(mv) * conversionFactor) : Math.round(Number(mv));
        return { ...row, OfferValue: offerValueAV };
      });

      setOfferFlowDialog(null);
      await ctx.rerun({ selectedRows: updatedSelectedRows, skipSubsetCheck: true });
    } finally {
      setUpdatingOffersToWorkup(false);
    }
  }, []);

  const handleMissingCompsSet = useCallback((index = 0) => {
    logger.log('🔍 [EVIDENCE TABLE] handleMissingCompsSet called with index:', index);
    const cases = offerFlowMissingCompsCasesRef.current || [];
    logger.log('🔍 [EVIDENCE TABLE] missingCompsCases length:', cases.length, 'cases:', cases);

    const target = cases[index];
    if (!target) {
      logger.warn('⚠️ [EVIDENCE TABLE] No target case found for missing comps index', index);
      return;
    }

    const targetParcelId = target.parcel_id || target.id;
    logger.log('🔍 [EVIDENCE TABLE] target parcel/id for missing comps:', targetParcelId);
    if (!targetParcelId) {
      logger.warn('⚠️ [EVIDENCE TABLE] Target case has no parcel_id/id:', target);
      return;
    }

    const row = rows.find(r => (r.parcel_id || r.id) === targetParcelId);
    logger.log('🔍 [EVIDENCE TABLE] resolved row for missing comps:', row);
    if (!row) {
      logger.warn('⚠️ [EVIDENCE TABLE] No DataGrid row found matching parcel/id:', targetParcelId);
      return;
    }

    logger.log('🔍 [EVIDENCE TABLE] Setting selectionModel to row id:', row.id);
    setSelectionModel(createRowSelectionFromIds([row.id]));

    if (!apiRef?.current) {
      logger.warn('⚠️ [EVIDENCE TABLE] apiRef.current not available for scroll');
      return;
    }

    const rowIndex = rows.findIndex(r => r.id === row.id);
    logger.log('🔍 [EVIDENCE TABLE] rowIndex for scrollToIndexes:', rowIndex);
    if (rowIndex < 0) {
      logger.warn('⚠️ [EVIDENCE TABLE] Could not find row index for id:', row.id);
      return;
    }

    try {
      logger.log('🔄 [EVIDENCE TABLE] Calling apiRef.current.scrollToIndexes({ rowIndex })');
      apiRef.current.scrollToIndexes({ rowIndex });
      logger.log('✅ [EVIDENCE TABLE] scrollToIndexes completed');
    } catch (err) {
      logger.error('❌ [EVIDENCE TABLE] Error during scrollToIndexes:', err);
    }
  }, [rows, setSelectionModel, apiRef]);

  // Send Offers + Workups workflow (Evidence Comparison context)
  const handleSendOffersAndWorkups = useCallback(async (
    workupType = 'fnmaSummary',
    workupOptions = null
  ) => {
    if (selectedRowIds.length === 0) {
      toast.error('Select at least one row to send offers and workups');
      return;
    }

    const selectedRowsFromTable = rows.filter(row => selectedRowIds.includes(row.id));

    if (selectedRowsFromTable.length === 0) {
      toast.error('No valid rows selected for offers and workups');
      return;
    }

    const negotiationObj = getNegotiationObj;
    const fullCases = negotiationObj?.cases ?? [];

    const selectedRows = buildSelectedRowsForOffers(selectedRowsFromTable, fullCases);

    evidenceOfferWorkupOptionsRef.current = workupOptions ?? workupOptionsFromDefaults(useOfferOptionsStore.getState());

    setIsSendingOffersAndWorkups(true);

    try {
      const rerun = async (overrides = {}) => {
        const nextSelectedRows = overrides.selectedRows || selectedRows;
        const nextSkipSubsetCheck = overrides.skipSubsetCheck || false;
        const nextSkipWorkupsAboveIfmvCheck = overrides.skipWorkupsAboveIfmvCheck || false;

        setIsSendingOffersAndWorkups(true);
        const nextResult = await handleOffersAndWorkupsEmailWorkflow({
          selectedRows: nextSelectedRows,
          allRows: fullCases,
          negotiationObj,
          user,
          workupType,
          skipSubsetCheck: nextSkipSubsetCheck,
          skipWorkupsAboveIfmvCheck: nextSkipWorkupsAboveIfmvCheck,
          RegenerateWorkups: false,
          workupOptions: evidenceOfferWorkupOptionsRef.current,
        });
        return { nextResult, nextSelectedRows };
      };

      offerFlowContextRef.current = {
        selectedRows,
        selectedRowsFromTable,
        fullCases,
        negotiationObj,
        workupType,
        rerun,
        openCasesNotSelected: [],
        workupOptions: evidenceOfferWorkupOptionsRef.current,
      };

      const { nextResult: result } = await rerun({ skipSubsetCheck: false });

      logger.log('📊 [EVIDENCE TABLE] Combined workflow result:', result);

      if (!result.success) {
        const ui = getOfferFlowDialogFromResult({ result, selectedRows });
        if (ui) {
          offerFlowContextRef.current.openCasesNotSelected = ui.context.openCases || [];
          if (ui.context.missingOffers) offerFlowMissingOffersWithWorkupRef.current = ui.context.missingOffers;
          if (ui.context.missingCompsCases) offerFlowMissingCompsCasesRef.current = ui.context.missingCompsCases;
          setOfferFlowDialog(ui.dialog);
          setIsSendingOffersAndWorkups(false);
          return;
        }
        toast.error(result.error || 'Validation failed for offers and workups', { autoClose: false });
        setIsSendingOffersAndWorkups(false);
        return;
      }

      await showGeneralizedEmailPreview(result.emailOptions, async (confirmed, emailPreviewData) => {
        if (!confirmed || !emailPreviewData) {
          setIsSendingOffersAndWorkups(false);
          return;
        }

        try {
          const sendResult = await sendEmailFromPreview(emailPreviewData);

          if (sendResult.success) {
            try {
              const emailData = emailPreviewData.emailData;
              if (emailData && emailData.data && Array.isArray(emailData.data)) {
                const parcelIds = emailData.data
                  .filter(item => item.parcel_id)
                  .map(item => item.parcel_id);

                if (parcelIds.length > 0) {
                  const markResult = await markOffersAsSent(parcelIds, negotiationObj, user);
                  if (!markResult.success) {
                    logger.warn('⚠️ [EVIDENCE TABLE] Failed to mark offers as sent:', markResult.error);
                  }
                }
              }
            } catch (error) {
              logger.error('❌ [EVIDENCE TABLE] Error marking offers as sent:', error);
            }

            {
              const wo = evidenceOfferWorkupOptionsRef.current;
              const batchAttach = wo.includeWorkups && wo.type === 'batch';
              toast.success(
                batchAttach
                  ? 'Offers and workups email sent successfully'
                  : wo.type === 'individual'
                    ? 'Offer email sent successfully (workup links in spreadsheet)'
                    : 'Offer email sent successfully'
              );
            }
          } else {
            toast.error(`Failed to send email: ${sendResult.error}`);
          }
        } catch (error) {
          logger.error('❌ [EVIDENCE TABLE] Error sending combined email:', error);
          toast.error(`Error sending email: ${error.message}`);
        } finally {
          setIsSendingOffersAndWorkups(false);
        }
      });
    } catch (error) {
      logger.error('❌ [EVIDENCE TABLE] Error in offers + workups workflow:', error);
      toast.error(error.message || 'Error running offers + workups workflow', { autoClose: false });
      setIsSendingOffersAndWorkups(false);
    }
  }, [rows, selectionModel, getNegotiationObj, user, showGeneralizedEmailPreview, sendEmailFromPreview, buildSelectedRowsForOffers]);

  const handleSendOffersOnly = useCallback(async () => {
    if (selectedRowIds.length === 0) {
      toast.error('Select at least one row to send offers', { autoClose: false });
      return;
    }
    const selectedRowsFromTable = rows.filter(row => selectedRowIds.includes(row.id));
    if (selectedRowsFromTable.length === 0) {
      toast.error('No valid rows selected', { autoClose: false });
      return;
    }
    const negotiationObj = getNegotiationObj;
    const fullCases = negotiationObj?.cases ?? [];
    const selectedRows = buildSelectedRowsForOffers(selectedRowsFromTable, fullCases);

    logger.log('selected rows coming in... change this structure!!', selectedRows);
    evidenceOfferWorkupOptionsRef.current = { includeWorkups: false };
    setIsSendingOffersAndWorkups(true);
    try {
      const rerun = async (overrides = {}) => {
        const nextSelectedRows = overrides.selectedRows || selectedRows;
        const nextSkipSubsetCheck = overrides.skipSubsetCheck || false;
        const nextSkipWorkupsAboveIfmvCheck = overrides.skipWorkupsAboveIfmvCheck || false;

        setIsSendingOffersAndWorkups(true);
        const nextResult = await handleOffersAndWorkupsEmailWorkflow({
          selectedRows: nextSelectedRows,
          allRows: fullCases,
          negotiationObj,
          user,
          workupType: 'fnmaSummary',
          skipSubsetCheck: nextSkipSubsetCheck,
          skipWorkupsAboveIfmvCheck: nextSkipWorkupsAboveIfmvCheck,
          workupOptions: evidenceOfferWorkupOptionsRef.current,
          RegenerateWorkups: false,
        });
        return { nextResult, nextSelectedRows };
      };

      offerFlowContextRef.current = {
        selectedRows,
        selectedRowsFromTable,
        fullCases,
        negotiationObj,
        workupType: 'fnmaSummary',
        rerun,
        openCasesNotSelected: [],
        workupOptions: evidenceOfferWorkupOptionsRef.current,
      };

      const { nextResult: result } = await rerun({ skipSubsetCheck: false });

      if (!result.success) {
        const ui = getOfferFlowDialogFromResult({ result, selectedRows });
        if (ui) {
          offerFlowContextRef.current.openCasesNotSelected = ui.context.openCases || [];
          if (ui.context.missingOffers) offerFlowMissingOffersWithWorkupRef.current = ui.context.missingOffers;
          if (ui.context.missingCompsCases) offerFlowMissingCompsCasesRef.current = ui.context.missingCompsCases;
          setOfferFlowDialog(ui.dialog);
          setIsSendingOffersAndWorkups(false);
          return;
        }
        toast.error(result.error || 'Failed to prepare offer email', { autoClose: false });
        setIsSendingOffersAndWorkups(false);
        return;
      }
      if (result.emailOptions) {
        await showGeneralizedEmailPreview(result.emailOptions, async (confirmed, emailPreviewData) => {
          if (!confirmed || !emailPreviewData) {
            setIsSendingOffersAndWorkups(false);
            return;
          }
          try {
            const sendResult = await sendEmailFromPreview(emailPreviewData);
            if (sendResult.success) {
              const emailData = emailPreviewData.emailData;
              if (emailData?.data && Array.isArray(emailData.data)) {
                const parcelIds = emailData.data.filter(item => item.parcel_id).map(item => item.parcel_id);
                if (parcelIds.length > 0) {
                  await markOffersAsSent(parcelIds, getNegotiationObj, user);
                }
              }
              toast.success('Offer email sent successfully');
            } else {
              toast.error(`Failed to send email: ${sendResult.error}`, { autoClose: false });
            }
          } catch (err) {
            logger.error('❌ [EVIDENCE TABLE] Error sending offer email:', err);
            toast.error(err?.message || 'Error sending email', { autoClose: false });
          } finally {
            setIsSendingOffersAndWorkups(false);
          }
        });
      } else {
        setIsSendingOffersAndWorkups(false);
      }
    } catch (error) {
      logger.error('❌ [EVIDENCE TABLE] Error in offer send:', error);
      toast.error(error.message || 'Error sending offers', { autoClose: false });
      setIsSendingOffersAndWorkups(false);
    }
  }, [rows, selectionModel, getNegotiationObj, user, showGeneralizedEmailPreview, sendEmailFromPreview, buildSelectedRowsForOffers]);

  const handleWorkupOptionsConfirm = useCallback(async ({ workupOptions, workupReportType }) => {
    setWorkupOptionsDialogOpen(false);
    if (!workupOptions.includeWorkups) {
      await handleSendOffersOnly();
    } else {
      await handleSendOffersAndWorkups(workupReportType, workupOptions);
    }
  }, [handleSendOffersAndWorkups, handleSendOffersOnly]);

  // Calculate min offer data (used for both checking count and actual execution)
  const calculateMinOfferData = useCallback(() => {
    return rows
      .filter(row => selectedRowIds.includes(row.id)) // Only selected rows
      .filter(row => !row._isSettled) // Filter out settled cases
      .map(row => {
        const conversionFactor = getConversionFactor(row.EQ, row.RAR);
        const selectedWorkupTypes = row.selectedWorkupTypes || [];
        const salesValueMV = row.SalesMarketValue; // Already in MV
        const assessmentValueMV = row.AssessmentMarketValue; // Already in MV
        
        // Get current offer value from the row (in AV terms) - for display only
        const currentOfferAV = getDisplayValue(row, 'OfferValue');
        const currentOfferAVNum = currentOfferAV !== null && currentOfferAV !== undefined && !isNaN(Number(currentOfferAV)) 
          ? Number(currentOfferAV) 
          : null;
        
        // Convert workup values from MV to AV for comparison (avoid rounding issues)
        const salesValueAV = salesValueMV !== null && salesValueMV !== undefined && conversionFactor > 0
          ? convertMVToAV(salesValueMV, row.EQ, row.RAR)
          : null;
        const assessmentValueAV = assessmentValueMV !== null && assessmentValueMV !== undefined && conversionFactor > 0
          ? convertMVToAV(assessmentValueMV, row.EQ, row.RAR)
          : null;
        
        let minValueAV = null;
        let calculation = '';
        
        // Helper function to filter out 0 values
        const filterNonZero = (values) => values.filter(v => v !== null && v !== undefined && v !== 0);
        
        // If both selected: min(sales, assessment) - compare in AV
        if (selectedWorkupTypes.includes('Unequal') && selectedWorkupTypes.includes('Excessive')) {
          const validValues = filterNonZero([salesValueAV, assessmentValueAV]);
          if (validValues.length > 0) {
            minValueAV = Math.min(...validValues);
            const valuesStr = validValues.join(', ');
            calculation = `min(${valuesStr}) = ${minValueAV}`;
          }
        }
        else if (selectedWorkupTypes.includes('Unequal') && !selectedWorkupTypes.includes('Excessive')) {
          const validValues = filterNonZero([salesValueAV]);
          if (validValues.length > 0) {
            minValueAV = validValues[0];
            calculation = `sales = ${minValueAV}`;
          }
        }
        else if (selectedWorkupTypes.includes('Excessive') && !selectedWorkupTypes.includes('Unequal')) {
          const validValues = filterNonZero([assessmentValueAV]);
          if (validValues.length > 0) {
            minValueAV = validValues[0];
            calculation = `assessment = ${minValueAV}`;
          }
        }
        
        // Convert minValueAV to MV for display only
        const minValueMV = minValueAV !== null && conversionFactor > 0
          ? convertAVToMV(minValueAV, row.EQ, row.RAR)
          : null;
        
        // Convert currentOfferAV to MV for display only
        const currentOfferMV = currentOfferAVNum !== null && conversionFactor > 0
          ? convertAVToMV(currentOfferAVNum, row.EQ, row.RAR)
          : null;
        
        // Always update to min value if minValue is not null
        const shouldUpdate = minValueAV !== null;
        
        return {
          parcelId: row.parcel_id,
          address: row.Address,
          rowIndex: row.RowIndex, // Grid row index number
          selectedWorkupTypes,
          currentOffer: currentOfferAVNum, // AV value for comparison and storage
          currentOfferMV, // MV value for display only
          salesValue: salesValueMV, // Keep MV for reference
          assessmentValue: assessmentValueMV, // Keep MV for reference
          minValue: minValueAV, // AV value for storage
          minValueMV, // MV value for display only
          calculation,
          shouldUpdate
        };
      })
      .filter(entry => entry.parcelId && entry.minValue !== null); // Filter out entries without parcel_id or minValue
  }, [rows, getDisplayValue, selectionModel]);

  // Handle Set Recommended button click
  const handleSetRecommended = useCallback(() => {
    if (selectedRowIds.length === 0) {
      toast.error('Please select at least one row', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }
    
    const newSelections = {};
    let updatedCount = 0;
    
    rows
      .filter(row => selectedRowIds.includes(row.id)) // Only selected rows
      .forEach(row => {
      // Skip settled cases
      if (row._isSettled) return;
      
      const recommended = calculateRecommendedSelections(row);
      const currentSelection = workupSelections[row.id] || row.selectedWorkupTypes || [];
      
      // Only update if different from current selection
      const recommendedStr = recommended.sort().join(',');
      const currentStr = currentSelection.sort().join(',');
      
      if (recommendedStr !== currentStr) {
        newSelections[row.id] = recommended;
        updatedCount++;
      } else if (workupSelections[row.id]) {
        // Keep existing selection even if it matches default
        newSelections[row.id] = workupSelections[row.id];
      }
      });
    
    setWorkupSelections(prev => ({
      ...prev,
      ...newSelections,
    }));
    
    if (updatedCount > 0) {
      toast.success(`Updated recommended selections for ${updatedCount} case${updatedCount !== 1 ? 's' : ''}`, {
        position: 'top-right',
        autoClose: 3000,
      });
    } else {
      toast.info('All cases already have recommended selections', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  }, [rows, workupSelections, calculateRecommendedSelections, selectionModel]);

  // Handle Select All Supporting button click
  // Selects ALL evidence types (sales or assessment) that are UNDER the IFMV for each property
  const handleSelectAllSupporting = useCallback(() => {
    if (selectedRowIds.length === 0) {
      toast.error('Please select at least one row', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }
    
    const newSelections = {};
    let updatedCount = 0;
    
    rows
      .filter(row => selectedRowIds.includes(row.id)) // Only selected rows
      .forEach(row => {
        // Skip settled cases
        if (row._isSettled) return;
        
        const ifmv = row.IFMV;
        // If IFMV is not available, skip this row
        if (ifmv === null || ifmv === undefined || ifmv === 0) {
          return;
        }
        
        const salesValue = row.SalesMarketValue;
        const assessmentValue = row.AssessmentMarketValue;
        const currentSelection = workupSelections[row.id] || row.selectedWorkupTypes || [];
        
        const supportingTypes = [];
        
        if (salesValue !== null && salesValue !== undefined && salesValue < ifmv) {
          supportingTypes.push('Unequal');
        }
        if (assessmentValue !== null && assessmentValue !== undefined && assessmentValue < ifmv) {
          supportingTypes.push('Excessive');
        }
        
        // Only update if different from current selection
        const supportingStr = supportingTypes.sort().join(',');
        const currentStr = currentSelection.sort().join(',');
        
        if (supportingStr !== currentStr) {
          newSelections[row.id] = supportingTypes;
          updatedCount++;
        } else if (workupSelections[row.id]) {
          // Keep existing selection even if it matches
          newSelections[row.id] = workupSelections[row.id];
        }
      });
    
    setWorkupSelections(prev => ({
      ...prev,
      ...newSelections,
    }));
    
    if (updatedCount > 0) {
      toast.success(`Selected all supporting evidence for ${updatedCount} case${updatedCount !== 1 ? 's' : ''}`, {
        position: 'top-right',
        autoClose: 3000,
      });
    } else {
      toast.info('All cases already have supporting evidence selected', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  }, [rows, workupSelections, selectionModel]);

  // Shared function to actually update the offers (used by both dialog confirmation and direct update)
  const handleConfirmSetMinOfferDirect = useCallback(async (offersList) => {
    if (!offersList || offersList.length === 0) {
      return;
    }

    // Set loading state
    setIsUpdatingOffers(true);

    // Create context for batch update
    const context = createOfferContext(user, getNegotiationObj, 'Manual');

    // Convert offers to the format expected by updateOffersBatch
    const batchOffers = offersList.map(offer => {
      // minValue is already in AV terms (no conversion needed)
      const offerValueAV = offer.minValue;

      return {
        parcelId: offer.parcelId,
        offerValue: offerValueAV,
        offeredBy: 'Aventine', // All offers in evidence comparison are Aventine offers
        offerDate: new Date().toISOString(),
        comments: null
      };
    });

    try {
      // Call batch update
      const result = await updateOffersBatch(batchOffers, context, {
        updateFrontend: true
      });

      if (result.success) {
        toast.success(
          `Successfully updated ${result.processedCount || offersList.length} offer${offersList.length !== 1 ? 's' : ''}`,
          {
            position: 'top-right',
            autoClose: 5000,
          }
        );
        
        setIsUpdatingOffers(false);
      } else {
        toast.error(
          result.error || 'Failed to update offers',
          {
            position: 'top-right',
            autoClose: 5000,
          }
        );
        setIsUpdatingOffers(false);
      }
    } catch (error) {
      logger.error('Error updating offers:', error);
      toast.error(
        error.message || 'Failed to update offers',
        {
          position: 'top-right',
          autoClose: 5000,
        }
      );
      setIsUpdatingOffers(false);
    }
  }, [user, getNegotiationObj]);

  // Handle Set Min Offer button click - check for existing offers first
  const handleSetMinOffer = useCallback(() => {
    if (selectedRowIds.length === 0) {
      toast.error('Please select at least one row', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }
    
    const minOfferData = calculateMinOfferData();
    
    // Get all offers that will be updated (all entries with minValue)
    const offersToUpdateList = minOfferData.filter(entry => entry.shouldUpdate === true);
    
    if (offersToUpdateList.length > 0) {
      // Show confirmation dialog with the offers list
      setOffersToUpdate(offersToUpdateList);
      setSetMinOfferDialogOpen(true);
    } else {
      // No offers to update - show info message
      toast.info('No offers can be updated. No minimum workup values available for selected cases.', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  }, [calculateMinOfferData, selectionModel]);

  // Handle dialog confirmation - actually update the offers
  const handleConfirmSetMinOffer = useCallback(async () => {
    if (offersToUpdate.length === 0 || isUpdatingOffers) {
      return;
    }

    await handleConfirmSetMinOfferDirect(offersToUpdate);
    
    // Close dialog
    setSetMinOfferDialogOpen(false);
  }, [offersToUpdate, isUpdatingOffers, handleConfirmSetMinOfferDirect]);

  // Handle dialog cancellation
  const handleCancelSetMinOffer = useCallback(() => {
    if (!isUpdatingOffers) {
      setSetMinOfferDialogOpen(false);
    }
  }, [isUpdatingOffers]);

  // Helper function to check if cell should be editable
  const shouldAllowEditing = useCallback((row) => {
    if (!row) return false;
    
    // Prevent editing if loading, not filed at SCAR, not active, or settled
    return !(row.loading || row.notFiledAtScar || !row.isActive || row._isSettled);
  }, []);

  // onCellClick handler to start edit for offer fields on single click
  // Early return for non-OfferValue fields to prevent unnecessary processing
  const handleCellClick = useCallback((params) => {
    // Early return for non-OfferValue fields - prevents any processing
    if (!params || params.field !== 'OfferValue') {
      return;
    }
    
    if (!params.id) {
      return;
    }
    
    if (!shouldAllowEditing(params.row)) {
      return;
    }

    // 1) Move grid's focus to this cell
    apiRef.current.setCellFocus(params.id, params.field);

    // 2) Start edit mode on next paint
    requestAnimationFrame(() => {
      try {
        apiRef.current.startCellEditMode({ id: params.id, field: params.field });
      } catch (error) {
        logger.log('Error starting edit mode:', error.message);
      }
    });
  }, [apiRef, shouldAllowEditing]);

  // Memoized row export processor
  const processRowForExport = useCallback((row) => {
    // Format WorkupSelection (selectedWorkupTypes) as array of strings
    const selectedWorkupTypes = row.selectedWorkupTypes || [];
    return {
      ...row,
      WorkupSelection: selectedWorkupTypes.length > 0 
        ? JSON.stringify(selectedWorkupTypes) 
        : '[]',
    };
  }, []);

  // Memoized row className getter
  const getRowClassNameMemo = useCallback((params) => {
    return params.row._isSettled ? 'settled-row' : '';
  }, []);

  return (
    <Box sx={{ height: '100%', width: '100%', p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Evidence Comparison
      </Typography>
      <Box sx={{ height: 'calc(100% - 80px)' }}>
        <DataGridPro
          apiRef={apiRef}
          rows={rows}
          columns={columns}
          loading={getIsFetching}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 25 },
            },
            columns: {
              columnVisibilityModel: COLUMN_VISIBILITY_MODEL,
            },
          }}
          pageSizeOptions={[25, 50, 100]}
          density="compact"
          disableColumnFilter={false}
          columnBufferPx={150}
          rowBufferPx={200}
          checkboxSelection
          rowSelectionModel={selectionModel}
          disableRowSelectionOnClick
          onRowSelectionModelChange={setSelectionModel}
          processRowForExport={processRowForExport}
          onCellClick={handleCellClick}
          slots={withSelectAllAwareCheckbox({
            toolbar: EvidenceComparisonToolbar,
            footer: EvidenceComparisonFooter,
          })}
          slotProps={{
            toolbar: {
              activeStage,
              setActiveStage,
              statusFilter,
              setStatusFilter,
              onSetEvidence: handleSetEvidence,
              isSettingEvidence,
              onSetMinOffer: handleSetMinOffer,
              onSetRecommended: handleSetRecommended,
              onSelectAllSupporting: handleSelectAllSupporting,
              muniCode: getNegotiationObj?.MuniCode,
              onSelectAllSales: handleSelectAllSales,
              onSelectAllAssessments: handleSelectAllAssessments,
              areAllSalesSelected,
              areAllAssessmentsSelected,
              onSendOffersClick: () => setWorkupOptionsDialogOpen(true),
              isSendingOffersAndWorkups,
            },
            footer: {
              selectedRowsCount: selectedRowIds.length,
            },
          }}
          getRowClassName={getRowClassNameMemo}
          getCellClassName={getCellClassName}
          sx={{
            // Styling for settled rows - gray them out
            '& .MuiDataGrid-row.settled-row': {
              backgroundColor: 'rgba(0, 0, 0, 0.02) !important',
              '& .MuiDataGrid-cell': {
                color: 'rgba(0, 0, 0, 0.38) !important',
              },
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.02) !important',
              }
            },
          }}
          showToolbar
          disableRowSelectionExcludeModel />
      </Box>

      <CaseReviewDialogs
        offerFlowDialog={offerFlowDialog}
        setOfferFlowDialog={setOfferFlowDialog}
        offerFlowSelectedRows={offerFlowDialog?.selectedRows || []}
        offerFlowOpenCases={offerFlowDialog?.openCases || []}
        offerFlowMissingOffersList={offerFlowDialog?.missingOffersList || []}
        offerFlowMissingCompsList={offerFlowDialog?.missingCompsList || []}
        handleSetOffersToWorkup={handleSetOffersToWorkup}
        updatingOffersToWorkup={updatingOffersToWorkup}
        onMissingCompsSet={handleMissingCompsSet}

        // Missing Comps Dialog (unused here)
        missingCompsDialogOpen={false}
        setMissingCompsDialogOpen={() => {}}
        missingCompsList={[]}
        handleProceedWithMissingComps={() => {}}

        // Missing Workup Values Dialog (disabled)
        missingWorkupDialogOpen={false}
        setMissingWorkupDialogOpen={() => {}}
        missingWorkupList={[]}

        workupsAboveIfmvDialogOpen={false}
        setWorkupsAboveIfmvDialogOpen={() => {}}
        workupsAboveIfmvList={[]}
        handleContinueWorkupsAboveIfmv={handleContinueWorkupsAboveIfmv}

        // Subset Warning Dialog (controlled via offerFlowDialog)
        subsetWarningDialogOpen={false}
        setSubsetWarningDialogOpen={() => {}}
        selectedRows={[]}
        openCases={[]}
        emailingOffers={isSendingOffersAndWorkups}
        handleSendSelectedClick={() => setOfferFlowDialog({ type: 'send_selected_confirm' })}
        handleSendAllFinalized={handleSendAllFinalized}

        // Send Selected Confirmation Dialog (controlled via offerFlowDialog)
        sendSelectedConfirmDialogOpen={false}
        setSendSelectedConfirmDialogOpen={() => {}}
        handleConfirmSendSelected={handleConfirmSendSelected}

        // Cancel dialogs (unused here)
        cancellingOffers={false}
        rowsToCancel={[]}

        unifiedCancelDialogOpen={false}
        setUnifiedCancelDialogOpen={() => {}}
        handleUnifiedCancelConfirm={() => {}}

        // Upload dialog (unused here)
        uploadDialogOpen={false}
        setUploadDialogOpen={() => {}}
        onFileUpload={() => {}}
        onUpdateOffers={() => {}}
        townView={false}

        // Do not file at SCAR dialog (unused here)
        assignmentDialogOpen={false}
        setAssignmentDialogOpen={() => {}}
        DoNotFileScarDialog={null}

        // Email preview dialog (unused here; EvidenceComparison uses generalized preview)
        emailPreviewDialogComponent={null}

        // Resend dialog (unused here)
        resendDialogOpen={false}
        setResendDialogOpen={() => {}}
        alreadySentCount={0}
        handleResendConfirm={() => {}}
      />

      {/* Set Min Offer Confirmation Dialog */}
      <StableMuiDialog
        open={setMinOfferDialogOpen}
        onClose={isUpdatingOffers ? undefined : handleCancelSetMinOffer}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ color: 'warning.main', fontWeight: 'bold' }}>
          ⚠️ Confirm Update Offers
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '1rem', mb: 2 }}>
            This will update {offersToUpdate.length} previously set offer{offersToUpdate.length !== 1 ? 's' : ''}:
          </DialogContentText>
          <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
            {offersToUpdate.map((offer) => {
              // Values are already in MV terms (currentOfferMV and minValue)
              // Display them directly in MV
              const formatValue = (value) => {
                if (value === null || value === undefined) return 'N/A';
                if (value === 0) return 'None';
                return `$${Math.round(value).toLocaleString()}`;
              };
              
              return (
                <Box
                  key={offer.parcelId}
                  sx={{
                    p: 1.5,
                    mb: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    backgroundColor: 'background.paper'
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    {offer.rowIndex !== null && offer.rowIndex !== undefined ? offer.rowIndex : 'N/A'}. {offer.address || offer.parcelId}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, fontSize: '0.875rem' }}>
                    <Typography variant="body2" color="text.secondary">
                      Old: {formatValue(offer.currentOfferMV)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 'medium' }}>
                      → New: {formatValue(offer.minValueMV)}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCancelSetMinOffer}
            color="primary"
            variant="outlined"
            disabled={isUpdatingOffers}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmSetMinOffer}
            color="warning"
            variant="contained"
            disabled={isUpdatingOffers}
            startIcon={isUpdatingOffers ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isUpdatingOffers ? 'Updating...' : 'Confirm'}
          </Button>
        </DialogActions>
      </StableMuiDialog>

      <SendSpreadsheetOffersDialog
        open={workupOptionsDialogOpen}
        onClose={() => setWorkupOptionsDialogOpen(false)}
        onConfirm={handleWorkupOptionsConfirm}
      />

      {/* Workup Viewer Dialog */}
      <WorkupViewerDialog
        open={workupViewerOpen}
        onClose={() => setWorkupViewerOpen(false)}
        files={workupViewerFiles}
        nyscefFiles={workupViewerNyscefFiles}
        address={workupViewerAddress}
        scarIndexNumber={workupViewerScarIndex}
        initialWorkupType={workupViewerType}
      />
      {GeneralizedEmailDialog}
    </Box>
  );
}

export default React.memo(EvidenceComparisonTable);

